import { useAuth } from '@auth/useAuth'
import AppPaths from '@globals/paths'
import { useCurrentMember } from '@globals/useCurrentMember'
import RouteLoading from '@shared/routing/RouteLoading'
import { extractSearchParamsForNext } from '@shared/utils/queryParams'
import { motion, useReducedMotion } from 'framer-motion'
import { Navigate, Outlet, createSearchParams, useLocation } from 'react-router'

const pageVariants = {
  initial: {
    opacity: 0,
  },
  in: {
    opacity: 1,
  },
  out: {
    opacity: 0,
  },
}

const pageTransition = {
  type: 'tween' as const,
  ease: 'easeInOut' as const,
  duration: 0.35,
}

export const TenantScopeRoutes = () => {
  const { isAuthenticated, isTenantScope } = useAuth()
  const { pathname, search } = useLocation()
  const prefersReducedMotion = useReducedMotion()
  // Note that having the current member will always be required on every page on our app.
  // We are opting to fetch it first and leave the page in a loading state
  // while it's fetching. This will ensure that if there are components requiring this, they
  // will not crash as a result of a null exception due to a slower than usual request from
  // this endpoint.
  const { isFetched } = useCurrentMember()
  if (isAuthenticated && !isFetched) return <RouteLoading />

  // grab the destination url here so that if the user is unauthenticated
  // we can set it as a search parameter for use after the login flow.
  // the tenant login page will handle this and send the user to the
  // intended destination after login, falling back to the Home page if it
  // isn't set.

  const { nextSearch, extractedSearchParams } = extractSearchParamsForNext(search, ['loginHint'])
  // Use only the pathname for next to prevent query params from being nested
  const deepLinkParams = createSearchParams({
    next: pathname + nextSearch,
  })

  const redirectPath = isAuthenticated
    ? `${AppPaths.user.tenants()}?${deepLinkParams}${extractedSearchParams}`
    : `${AppPaths.anon.userLogin()}?${deepLinkParams}${extractedSearchParams}`

  const content = isTenantScope ? <Outlet /> : <Navigate to={redirectPath} />

  // Respect the user's reduced-motion preference by skipping the fade
  // transition that otherwise wraps every tenant navigation.
  if (prefersReducedMotion) return content

  return (
    <motion.div
      key={pathname}
      initial="initial"
      animate="in"
      variants={pageVariants}
      transition={pageTransition}>
      {content}
    </motion.div>
  )
}
